/**
 * Rate Limit Error Class
 *
 * Why: Specialized handling for rate limiting (429)
 * What: Error class for too many requests
 *
 * Benefits:
 *      - Automatic retry-after handling
 *      - User friendly rate limit messaging
 *      - Proper backoff behaviour
 *
 * Use Cases:
 *      - Api rate limiting
 *      - Brute force protection
 *      - Response usage limit
 */

import { AppError, type AppErrorOptions } from "./base/AppError";
import { ErrorCategory, ErrorSeverity } from "../types";
import { ERROR_CODES, ERROR_MESSAGES } from "../constants";

/**
 * Options specific to rate limit errors
 */

export interface RateLimitErrorOptions extends Omit<
  AppErrorOptions,
  "statusCode"
> {
  retryAfterSeconds?: number;
  limit?: number;
  // Number of requests made
  current?: number;
  // Time window for the limit (e.g per minute)
  window?: string;
}

/**
 * Rate Limit Error
 *
 * Represents rate limiting (429 Too many requests)
 * Includes retry timing information for proper backoff.
 */
export class RateLimitError extends AppError {
  /**
   * Seconds until retry is allowed
   * From the retry after response header
   */
  public readonly retryAfterSeconds?: number;
  /** THe rate limit that was exceeded */
  public readonly limit?: number;
  /**
   * Current number of requests made
   */
  public readonly current?: number;
  /**
   * Time window for rate limit
   */
  public readonly window?: string;

  constructor(
    message: string = ERROR_MESSAGES.RATE_LIMIT_EXCEEDED,
    options: RateLimitErrorOptions = {},
  ) {
    super(message, {
      statusCode: 429,
      code: options.code ?? ERROR_CODES.RATE_LIMIT_EXCEEDED,
      category: ErrorCategory.RATE_LIMIT,
      severity: options.severity ?? ErrorSeverity.MEDIUM,
      isOperational: true,
      isRetryable: true, // Rate limit errros are retryable after waiting
      context: options.context,
      cause: options.cause,
    });

    this.retryAfterSeconds = options.retryAfterSeconds;
    this.limit = options.limit;
    this.current = options.current;
    this.window = options.window;
  }

  /**
   * Create from Retry-After header value
   * @param retryAfter - Value from Retry-After header(seconds or date string)
   */

  static fromRetryAfterHeader(
    retryAfter: string | number | undefined,
    cause?: unknown,
  ): RateLimitError {
    let retryAfterSeconds: number | undefined;

    if (typeof retryAfter === "number") {
      retryAfterSeconds = retryAfter;
    } else if (typeof retryAfter === "string") {
      // could be seconds or HTTP date
      const parsed = parseInt(retryAfter, 10);
      if (!isNaN(parsed)) {
        retryAfterSeconds = parsed;
      } else {
        // Try to parse as date
        const date = new Date(retryAfter);
        if (!isNaN(date.getTime())) {
          retryAfterSeconds = Math.ceil((date.getTime() - Date.now()) / 1000);
        }
      }
    }
    return new RateLimitError(ERROR_MESSAGES.RATE_LIMIT_EXCEEDED, {
      retryAfterSeconds,
      cause,
    });
  }
  /**
   * Get time until retry is allowed (in milliseconds)
   * Returns 0 if retry is already allowed
   */
  public getRetryAfterMs(): number {
    if (!this.retryAfterSeconds || this.retryAfterSeconds <= 0) {
      return 0;
    }
    return this.retryAfterSeconds * 1000;
  }

  /**
   * Get human readable retry message
   */
  public getRetryMessage(): string {
    if (!this.retryAfterSeconds) {
      return "Please try again later.";
    }
    if (this.retryAfterSeconds < 60) {
      return `Please try again in ${this.retryAfterSeconds} seconds.`;
    }
    const minutes = Math.ceil(this.retryAfterSeconds / 60);
    return `Please try agin in ${minutes} minutes${minutes > 1 ? "s" : ""}.`;
  }

  /** Get Date when retry is allowed */
  public getRetryAt(): Date {
    const ms = this.getRetryAfterMs();
    return new Date(Date.now() + ms);
  }
}
